/**
 * Semantic tokens reference theme CSS variables (`--ui-*`).
 * Legacy `COLORS` mirrors `constants/style/color` for backward compatibility.
 */

const varRef = (name: `--ui-${string}`): string => `var(${name})`;

export const semanticVars = {
  background: varRef('--ui-background'),
  backgroundMuted: varRef('--ui-background-muted'),
  surface: varRef('--ui-surface'),
  surfaceElevated: varRef('--ui-surface-elevated'),

  text: varRef('--ui-text'),
  textMuted: varRef('--ui-text-muted'),
  textSubtle: varRef('--ui-text-subtle'),
  textInverse: varRef('--ui-text-inverse'),
  textDisabled: varRef('--ui-text-disabled'),

  border: varRef('--ui-border'),
  borderMuted: varRef('--ui-border-muted'),
  borderStrong: varRef('--ui-border-strong'),

  interactive: varRef('--ui-interactive'),
  interactiveHover: varRef('--ui-interactive-hover'),
  interactiveActive: varRef('--ui-interactive-active'),
  onInteractive: varRef('--ui-on-interactive'),

  focusRing: varRef('--ui-focus-ring'),

  statusSuccess: varRef('--ui-status-success'),
  statusSuccessMuted: varRef('--ui-status-success-muted'),
  statusWarning: varRef('--ui-status-warning'),
  statusWarningMuted: varRef('--ui-status-warning-muted'),
  statusDanger: varRef('--ui-status-danger'),
  statusDangerMuted: varRef('--ui-status-danger-muted'),
  statusInfo: varRef('--ui-status-info'),
  statusInfoMuted: varRef('--ui-status-info-muted'),

  overlay: varRef('--ui-overlay'),
  shadowColor: varRef('--ui-shadow-color'),
} as const;

export const COLORS = {
  Purple: '#7B53D1',
  DarkPurple: '#7D55D3',
  LightPurple: '#F0E9FF',
  Iris: '#384DD6',
  Blue: '#071360',
  LightBlue: '#E3E6F6',
  Red: '#BA2323',
  LightRed: '#FFE0E0',
  Green: '#13A83D',
  LightGreen: '#E5EFE8',
  Magnolia: '#F0F2FF',
  SkyBlue: '#BBC2FF',

  White: '#FFFFFF',
  Black: '#161616',
  Gray: '#595959',
  Silver: '#717171',
  LightGray: '#D7D7D7',
  SpanishGray: '#969696',
  OffWhite: '#F3F3F3',
  CulturedWhite: '#EDEDED',

  NewPurple: '#8B83BA',
  Pink: '#F178B6',
  LightPink: '#FCDDEC',
  CornSilk: '#fff3dc',
  PinkLace: '#FFF0FA',
  Yellow: '#F9DC47',

  Gray10: '#d2d2d2',
  Gray20: '#f2f2f2',
  Gray30: '#4D4D4D',
  Gray40: '#C4C4C4',
  Gray50: '#A6A6A6',
  Gray60: '#333333',
  Gray70: '#f1f1f1',
  Gray80: '#D9D9D9',
  Gray90: '#666666',
  Gray100: '#b3b3b3',
  Gray200: '#e6e6e6',
  Gray300: '#e5e5e5',
  Gray400: '#cfcfcf',
  Gray500: '#cbbac9',
  Gray600: '#6d6d6d',
  Gray700: '#88ab92',
  Gray800: '#e9e9e9',
  Gray900: '#b2b2b2',
  Gray1000: '#7A7A7A',
  Gray1010: '#EAEEFB',
  Gray1020: '#FAFAFA',

  DefaultShadow: 'rgba(203, 186, 201, 0.12)',
  SoftShadow: 'rgba(109, 109, 109, 0.15)',
  RadioShadow: 'rgba(136, 171, 146, 0.31)',
  TagShadow: 'rgba(233, 233, 233, 0.5)',
  FloatingButtonShadow: 'rgba(56, 77, 214, 0.24)',
  PressedButtonShadow: 'rgba(56, 77, 214, 0.44)',
  FloatingButtonGrayShadow: 'rgba(179, 179, 179, 0.24)',
  PressedButtonGrayShadow: 'rgba(178, 178, 178, 0.44)',
  CardShadow: 'rgba(210, 210, 210, 0.28)',
  PurpleBorder: 'rgba(123, 83, 209, 0.25)',
  ModalBackdrop: 'rgba(0, 0, 0, 0.85)',
  RedLightTransparent: 'rgba(235, 84, 84, 0.15)',
  Grey100Transparent: '#d9d9d91a',
  Grey200Transparent: '#d9d9d933',
  Grey300Transparent: '#d9d9d94d',
  Grey400Transparent: '#d9d9d966',
  Grey500Transparent: '#d9d9d980',
  Grey600Transparent: '#d9d9d999',
  Grey700Transparent: '#d9d9d9b3',
  Grey800Transparent: '#d9d9d9cc',
  Grey900Transparent: '#d9d9d9e6',
  Secondary: '#5BBCC9',
  SecondaryDark: '#5ABDCA',
  SecondaryRed: '#D84545',
} as const;

export type ColorKey = keyof typeof COLORS;

const expandShortHex = (hex: string): string => {
  if (hex.length === 3) {
    return hex
      .split('')
      .map((c) => c + c)
      .join('');
  }
  return hex;
};

/**
 * Convert `#rgb` / `#rrggbb` to `rgba(r, g, b, a)`.
 */
export const getRgbaValue = (hex: string, alpha = 1): string => {
  let body = hex.trim().replace(/^#/, '');
  if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(body)) {
    throw new Error(`getRgbaValue: invalid hex "${hex}"`);
  }
  body = expandShortHex(body);
  const r = Number.parseInt(body.slice(0, 2), 16);
  const g = Number.parseInt(body.slice(2, 4), 16);
  const b = Number.parseInt(body.slice(4, 6), 16);
  const a = Math.min(1, Math.max(0, alpha));
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};
